import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useReducedMotion } from 'motion/react'
import { Package, Gem, Loader2, AlertCircle, RotateCcw, FileText, Check, ChevronsRight, LocateFixed, ListChecks, X } from 'lucide-react'
import confetti from 'canvas-confetti'
import classNames from 'classnames'
import useCasesStore from '../store/useCasesStore'
import useAppStore from '../store/useAppStore'
import useInventoryStore from '../store/useInventoryStore'
import { inventoryApi } from '../services'
import { formatPrice } from '../utils/formatters'
import { hapticImpact, hapticNotify, hapticSelectionChanged } from '../utils/haptic'
import { toast } from 'react-hot-toast'
import useAudioManager from '../hooks/useAudioManager'
import useUpdateBlocker from '../hooks/useUpdateBlocker'

const profitPercent = (item) => Number.parseFloat(String(item?.win?.profit ?? '0').replace(',', '.')) || 0
const itemId = (item) => item?.id || item?.inventory_id
const itemPrice = (item) => Number(item?.win?.cost) || 0
const actionButtonClassName = 'flex min-h-[58px] flex-wrap items-center justify-center gap-1.5 rounded-[10px] px-4 py-3 text-center text-[15px] font-extrabold leading-[1.3] transition-[filter,border-color] duration-200 enabled:hover:brightness-[1.14] disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none'
const actionLinkClassName = 'inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-lg px-2 py-2 text-sm font-semibold text-text-secondary hover:text-white disabled:opacity-50'
const smallButtonClassName = 'inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-lg border border-white/10 px-3 text-sm font-semibold text-white hover:bg-white/5 disabled:opacity-50'

const CaseOpeningMulti = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const reducedMotion = useReducedMotion()

  const {
    currentCase,
    loadCaseDetails,
    openCaseMultiple,
    isOpeningMultiple,
    openingMultipleResults,
    error,
    clearMultipleResults,
    clearError,
    loadCases
  } = useCasesStore()
  const { loadProfile } = useAppStore()

  const caseId = id
  const caseData = currentCase

  // Получаем количество открытий из state навигации
  const openCount = location.state?.count || 2

  // Состояния для анимации появления предметов
  const [displayedItems, setDisplayedItems] = useState([]) // Массив уже показанных предметов
  const [currentItemIndex, setCurrentItemIndex] = useState(0) // Индекс текущего показываемого предмета
  const [isAnimationComplete, setIsAnimationComplete] = useState(false) // Флаг завершения анимации
  const [isIntroStarted, setIsIntroStarted] = useState(false)
  const [isFollowing, setIsFollowing] = useState(true)
  const [panelHeight, setPanelHeight] = useState(300)
  const panelRef = useRef(null)
  const batchCostRef = useRef(null)
  const skipRequestedRef = useRef(false)
  const followingRef = useRef(true)

  // Состояния для продажи предметов
  const [soldItemIds, setSoldItemIds] = useState(new Set()) // Set проданных ID предметов
  const [selectedSaleIds, setSelectedSaleIds] = useState(new Set())
  const [isSellingSelected, setIsSellingSelected] = useState(false)
  const isSellingSelectedRef = useRef(false)
  useUpdateBlocker(() => isSellingSelectedRef.current || isOpeningMultiple || (!error && !isAnimationComplete))

  // Флаги для предотвращения повторного воспроизведения
  const hasPlayedStartSound = useRef(false)
  const hasPlayedFinalSound = useRef(false)
  const hasStartedOpening = useRef(false)
  const cardRefs = useRef([])

  // Инициализация Audio Manager через глобальный audioService
  const { playSound, stopSound } = useAudioManager({
    win: 'caseMultiWin',
    sell: 'sell',
    start: 'caseMultiStart',
    goodWin: 'caseMultiWinGood',
    final: 'caseMultiFinal'
  })

  // Функция для сокращения состояния износа
  const shortenWear = (text) => text
    .replace(/\(Factory New\)/gi, 'FN')
    .replace(/\(Minimal Wear\)/gi, 'MW')
    .replace(/\(Field-Tested\)/gi, 'FT')
    .replace(/\(Well-Worn\)/gi, 'WW')
    .replace(/\(Battle-Scarred\)/gi, 'BS')

  // Остановка всех звуков при уходе со страницы
  useEffect(() => () => {
    stopSound('start')
    stopSound('win')
    stopSound('goodWin')
    stopSound('final')
  }, [stopSound])

  // Проверка легитимности открытия
  useEffect(() => {
    if (!location.state?.allowOpening) {
      navigate('/cases', { replace: true })
      return
    }
    window.history.replaceState({}, document.title)
  }, [location, navigate])

  // Звуки теперь управляются через useAudioManager (Web Audio API)
  // Предзагрузка происходит автоматически в хуке

  // Очистка результатов при монтировании компонента
  useEffect(() => {
    clearMultipleResults()
    clearError()
    hasPlayedStartSound.current = false
    hasPlayedFinalSound.current = false
    hasStartedOpening.current = false

    // Сбрасываем флаги при размонтировании для следующего открытия
    return () => {
      hasPlayedStartSound.current = false
      hasPlayedFinalSound.current = false
      hasStartedOpening.current = false
    }
  }, [clearMultipleResults, clearError])

  // Загрузка деталей кейса
  useEffect(() => {
    if (caseId && !currentCase) loadCaseDetails(caseId)
  }, [caseId, currentCase, loadCaseDetails])

  // Функция открытия кейсов
  const handleOpenCases = useCallback(async () => {
    try {
      await openCaseMultiple(caseId, openCount)
    } catch (error) {
      console.error('Failed to open cases:', error)
    }
  }, [openCaseMultiple, caseId, openCount])

  // Запуск открытия кейсов после загрузки деталей
  useEffect(() => {
    if (caseData && !isOpeningMultiple && !openingMultipleResults && !hasStartedOpening.current) {
      hasStartedOpening.current = true
      const price = caseData.is_private ? caseData.price_discounted ?? caseData.price : caseData.price
      batchCostRef.current = price != null && Number.isFinite(Number(price)) ? Number(price) * openCount : null
      handleOpenCases()
    }
  }, [caseData, isOpeningMultiple, openingMultipleResults, handleOpenCases, openCount])

  useEffect(() => {
    if (error) hapticNotify('error')
  }, [error])

  useEffect(() => {
    if (!openingMultipleResults?.length) return
    setSelectedSaleIds(new Set(openingMultipleResults.filter(item => itemId(item) && !item.only_withdraw && profitPercent(item) < 10).map(itemId)))
  }, [openingMultipleResults])

  // Функция воспроизведения звука начала открытия когда получены результаты
  useEffect(() => {
    if (!openingMultipleResults?.length || hasPlayedStartSound.current || reducedMotion || isAnimationComplete) return undefined
    let soundFrame
    const paintFrame = window.requestAnimationFrame(() => {
      soundFrame = window.requestAnimationFrame(() => {
        if (skipRequestedRef.current) return
        playSound('start')
        hapticImpact('medium')
        hasPlayedStartSound.current = true
        setIsIntroStarted(true)
      })
    })
    return () => {
      window.cancelAnimationFrame(paintFrame)
      window.cancelAnimationFrame(soundFrame)
    }
  }, [openingMultipleResults, playSound, reducedMotion, isAnimationComplete])

  // Воспроизведение финального звука после завершения анимации
  useEffect(() => {
    if (!isAnimationComplete || hasPlayedFinalSound.current) return undefined
    hasPlayedFinalSound.current = true
    hapticNotify('success')
    const timer = window.setTimeout(() => playSound('final'), reducedMotion ? 0 : 350)
    return () => window.clearTimeout(timer)
  }, [isAnimationComplete, playSound, reducedMotion])

  // Анимация появления предметов по одному
  useEffect(() => {
    if (!openingMultipleResults?.length || isAnimationComplete) return undefined

    if (reducedMotion) {
      setDisplayedItems(openingMultipleResults)
      setCurrentItemIndex(openingMultipleResults.length)
      setIsAnimationComplete(true)
      useInventoryStore.getState().loadInventory()
      return undefined
    }

    if (!isIntroStarted) return undefined

    // Если все предметы уже показаны
    if (currentItemIndex >= openingMultipleResults.length) {
      const lastIsGood = profitPercent(openingMultipleResults[currentItemIndex - 1]) >= 10
      const timer = window.setTimeout(() => {
        setIsAnimationComplete(true)
        // Обновляем инвентарь после показа всех предметов
        useInventoryStore.getState().loadInventory()
      }, lastIsGood ? 1450 : 750)
      return () => window.clearTimeout(timer)
    }

    // Задержка перед показом следующего предмета
    const previousIsGood = currentItemIndex > 0 && profitPercent(openingMultipleResults[currentItemIndex - 1]) >= 10
    const timer = window.setTimeout(() => {
      if (skipRequestedRef.current) return
      const newItem = openingMultipleResults[currentItemIndex]
      if (currentItemIndex === 0) stopSound('start')
      setDisplayedItems(prev => [...prev, newItem])
      setCurrentItemIndex(prev => prev + 1)

      // Проверяем профит
      if (profitPercent(newItem) >= 10) {
        playSound('goodWin')
        hapticImpact('heavy')
        triggerConfetti()
      } else {
        playSound('win')
        hapticImpact('light')
      }
    }, currentItemIndex === 0 ? 3000 : previousIsGood ? 1450 : 900)

    return () => window.clearTimeout(timer)
  }, [currentItemIndex, openingMultipleResults, isAnimationComplete, isIntroStarted, reducedMotion, playSound, stopSound])

  useEffect(() => {
    if (!openingMultipleResults?.length || currentItemIndex === 0 || isAnimationComplete || !isFollowing) return undefined
    const frame = window.requestAnimationFrame(() => {
      if (!followingRef.current || skipRequestedRef.current) return
      const card = cardRefs.current[currentItemIndex - 1]
      if (!card) return
      const bounds = card.getBoundingClientRect()
      const visibleBottom = window.innerHeight - panelHeight - 16
      if (bounds.top >= 16 && bounds.bottom <= visibleBottom) return
      const top = window.scrollY + bounds.top - Math.max(16, (visibleBottom - bounds.height) / 2)
      window.scrollTo({ top: Math.max(0, top), behavior: reducedMotion ? 'auto' : 'smooth' })
    })
    return () => window.cancelAnimationFrame(frame)
  }, [currentItemIndex, openingMultipleResults, reducedMotion, isAnimationComplete, isFollowing, panelHeight])

  useEffect(() => {
    if (!openingMultipleResults?.length || isAnimationComplete) return undefined
    const stopFollowing = () => {
      if (!followingRef.current) return
      followingRef.current = false
      setIsFollowing(false)
      window.scrollTo({ top: window.scrollY, behavior: 'instant' })
    }
    const onKeyDown = (event) => {
      if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key) && !event.target.closest?.('button, input, select, textarea, a, [contenteditable]')) stopFollowing()
    }
    const onPointerDown = (event) => {
      if (event.pointerType === 'mouse' && event.clientX >= document.documentElement.clientWidth) stopFollowing()
    }
    window.addEventListener('wheel', stopFollowing, { passive: true })
    window.addEventListener('touchmove', stopFollowing, { passive: true })
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('pointerdown', onPointerDown)
    return () => {
      window.removeEventListener('wheel', stopFollowing)
      window.removeEventListener('touchmove', stopFollowing)
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('pointerdown', onPointerDown)
    }
  }, [openingMultipleResults, isAnimationComplete])

  useEffect(() => {
    const panel = panelRef.current
    if (!panel) return undefined
    const updateHeight = () => setPanelHeight(Math.ceil(panel.getBoundingClientRect().height))
    updateHeight()
    const observer = new ResizeObserver(updateHeight)
    observer.observe(panel)
    return () => observer.disconnect()
  }, [openingMultipleResults, isAnimationComplete, error])

  const handleSkipReveal = () => {
    if (!openingMultipleResults?.length || isAnimationComplete) return
    skipRequestedRef.current = true
    followingRef.current = false
    setIsFollowing(false)
    window.scrollTo({ top: window.scrollY, behavior: 'instant' })
    stopSound('start')
    stopSound('win')
    stopSound('goodWin')
    hapticImpact('light')
    setDisplayedItems(openingMultipleResults)
    setCurrentItemIndex(openingMultipleResults.length)
    setIsAnimationComplete(true)
    useInventoryStore.getState().loadInventory()
  }

  const handleFollowReveal = () => {
    hapticImpact('light')
    followingRef.current = true
    setIsFollowing(true)
  }

  // Функция запуска конфетти
  const triggerConfetti = () => {
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#00FF7F', '#4EFFC6', '#FFD700', '#FF6B6B', '#4ECDC4']
    })
  }

  const toggleSaleSelection = (item) => {
    const idToToggle = itemId(item)
    if (!isAnimationComplete || isSellingSelected || !idToToggle || soldItemIds.has(idToToggle)) return
    hapticSelectionChanged()
    setSelectedSaleIds(prev => {
      const next = new Set(prev)
      if (next.has(idToToggle)) next.delete(idToToggle)
      else next.add(idToToggle)
      return next
    })
  }

  const handleToggleAll = () => {
    if (isSellingSelected) return
    hapticSelectionChanged()
    setSelectedSaleIds(allSelected ? new Set() : new Set(saleableItems.map(itemId)))
  }

  const handleSellSelected = async () => {
    if (isSellingSelectedRef.current || selectedItems.length === 0) return
    const ids = selectedItems.map(itemId)
    isSellingSelectedRef.current = true
    hapticImpact('medium')
    setIsSellingSelected(true)
    try {
      const response = await inventoryApi.saleSelectedItems(ids)
      if (response.error) throw new Error(response.error)
      setSoldItemIds(prev => new Set([...prev, ...ids]))
      setSelectedSaleIds(prev => {
        const next = new Set(prev)
        ids.forEach(id => next.delete(id))
        return next
      })
      playSound('sell')
      hapticNotify('success')
      if (!response.alert) toast.success(t('caseOpening.soldFor', { amount: formatPrice(selectedPrice) }))
      await Promise.allSettled([loadProfile(), useInventoryStore.getState().loadInventory()])
    } catch (error) {
      console.error('Sell selected error:', error)
      toast.error(t('inventory.sellAllError'))
      hapticNotify('error')
    } finally {
      isSellingSelectedRef.current = false
      setIsSellingSelected(false)
    }
  }

  // Обработчик "Открыть ещё"
  const handleOpenAgain = async () => {
    hapticImpact('light')
    await loadProfile()
    loadCases()
    navigate(`/cases/${id}`)
  }

  // Обработчик "В инвентарь"
  const handleBackToInventory = () => {
    hapticImpact('light')
    clearMultipleResults()
    useInventoryStore.getState().loadInventory()
    navigate('/inventory')
  }

  // Показываем ошибку
  if (error) {
    return <div className="min-h-[60vh] bg-dark-primary text-white flex items-center justify-center p-6">
      <div className="text-center max-w-md">
        <AlertCircle size={64} className="mx-auto text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-white mb-2">{t('caseOpeningMulti.openingError')}</h2>
        <p className="text-text-secondary mb-6">{error}</p>
        <div className="space-y-3">
          <button type="button" onClick={() => { hapticImpact('medium'); handleOpenCases() }} className="w-full bg-accent-primary text-dark-primary font-bold py-3 rounded-lg">{t('caseOpeningMulti.retryOpening')}</button>
          <button type="button" onClick={() => { hapticImpact('light'); navigate('/cases') }} className="w-full bg-transparent border border-text-secondary text-text-secondary font-semibold py-3 rounded-lg">{t('caseOpeningMulti.returnToCases')}</button>
        </div>
      </div>
    </div>
  }

  // Показываем загрузку
  if (!caseData || isOpeningMultiple || !openingMultipleResults?.length) {
    return <div className="min-h-[60vh] bg-dark-primary text-white flex items-center justify-center"><Loader2 className="animate-spin text-accent-primary" size={48} /></div>
  }

  const remainingItems = openingMultipleResults.filter(item => !soldItemIds.has(itemId(item)))
  const saleableItems = displayedItems.filter(item => itemId(item) && !item.only_withdraw && !soldItemIds.has(itemId(item)))
  const selectedItems = saleableItems.filter(item => selectedSaleIds.has(itemId(item)))
  const selectedPrice = selectedItems.reduce((sum, item) => sum + itemPrice(item), 0)
  const allSelected = saleableItems.length > 0 && selectedItems.length === saleableItems.length
  const totalPrice = openingMultipleResults.reduce((sum, item) => sum + itemPrice(item), 0)
  const batchCost = batchCostRef.current
  const netResult = batchCost == null ? null : Math.round((totalPrice - batchCost) * 100) / 100
  const progress = Math.round(currentItemIndex / openingMultipleResults.length * 100)

  return <main className="min-h-screen bg-[radial-gradient(ellipse_70%_320px_at_50%_22%,rgba(0,255,127,.045),transparent),linear-gradient(#080b10,#080b10)] px-4 pt-[35px] text-[#f1f4f6] max-[540px]:px-3 max-[540px]:pt-[25px]" style={{ paddingBottom: panelHeight + 24 }}>
    <div className="mx-auto max-w-[1160px]">
      <section aria-label={t(isAnimationComplete ? 'caseOpeningMulti.allCasesOpened' : 'caseOpeningMulti.openingCases')}>
        {remainingItems.length === 0 && <div role="status" className="flex min-h-[240px] flex-col items-center justify-center gap-3 text-text-secondary"><Check size={36} className="text-accent-primary" /><p className="text-sm font-semibold">{t('caseOpeningMulti.allSold')}</p></div>}
        <div className="relative z-[1] grid w-full grid-cols-[repeat(var(--vault-columns),minmax(0,205px))] justify-center gap-[13px] max-[1023px]:grid-cols-[repeat(var(--vault-tablet-columns),minmax(0,205px))] max-[540px]:grid-cols-[repeat(var(--vault-mobile-columns),minmax(0,205px))] max-[540px]:gap-[9px]" style={{ '--vault-columns': Math.max(1, Math.min(5, remainingItems.length)), '--vault-tablet-columns': Math.max(1, Math.min(3, remainingItems.length)), '--vault-mobile-columns': Math.max(1, Math.min(2, remainingItems.length)) }}>
          {openingMultipleResults.map((item, index) => {
            if (soldItemIds.has(itemId(item))) return null
            const revealed = index < currentItemIndex
            const name = shortenWear(item?.win?.name || '')
            const [weapon, ...finish] = name.split('|')
            const selectable = revealed && isAnimationComplete && !item.only_withdraw && Boolean(itemId(item))
            const highlighted = profitPercent(item) >= 10
            const selected = selectedSaleIds.has(itemId(item))
            return <div
              key={itemId(item) || index}
              ref={node => { cardRefs.current[index] = node }}
              role="group"
              aria-label={revealed ? `${name}, ${formatPrice(itemPrice(item))}` : `${index + 1}`}
              className={classNames(
                'relative flex h-[226px] min-h-0 min-w-0 w-full scroll-my-6 flex-col overflow-hidden rounded border-2 text-left transition-[border-color,box-shadow,transform] duration-[450ms] max-[540px]:h-[200px] motion-reduce:transition-none',
                revealed && !skipRequestedRef.current && 'motion-safe:animate-vault-reveal',
                revealed ? 'border-[color:var(--rarity)] shadow-[inset_0_0_0_5px_#141412,inset_0_0_0_6px_#675741,0_18px_24px_rgba(0,0,0,.4)] after:absolute after:inset-x-[12%] after:bottom-0 after:h-0.5 after:bg-[var(--rarity)] after:shadow-[0_0_18px_var(--rarity)]' : 'border-[#39564a] bg-[radial-gradient(circle_at_50%_48%,#1e332a,#141a18_70%)] shadow-[inset_0_0_0_5px_#111815,inset_0_0_0_6px_#385246,0_18px_24px_rgba(0,0,0,.4)]',
                revealed && (highlighted ? '[--rarity:#edc484] [--rarity-rgb:237,196,132] bg-[radial-gradient(circle_at_50%_48%,rgba(var(--rarity-rgb),.27),#191815_80%)]' : '[--rarity:#85c8e3] [--rarity-rgb:133,200,227] bg-[radial-gradient(circle_at_50%_48%,rgba(var(--rarity-rgb),.24),#111923_80%)]'),
                selectable && selected && 'outline outline-2 outline-offset-2 outline-accent-primary',
                selectable && 'cursor-pointer'
              )}
            >
              {selectable && <input className="peer absolute inset-0 z-[2] m-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-wait" type="checkbox" checked={selected} onChange={() => toggleSaleSelection(item)} disabled={isSellingSelected} aria-label={`${t('inventory.sell')}: ${name}`} />}
              <span className={classNames('pointer-events-none absolute -inset-y-full -inset-x-[180%] -translate-x-[35%] bg-[linear-gradient(105deg,transparent_43%,rgba(255,255,255,.24)_50%,transparent_57%)] opacity-0', revealed && !skipRequestedRef.current && 'motion-safe:animate-prize-sweep')} />
              {revealed && <span className="relative z-[1] flex min-h-[42px] flex-[0_0_42px] items-start justify-between gap-[5px] px-[11px] pt-2 text-[10px] font-bold tracking-[.11em] text-[rgba(220,229,235,.54)] peer-focus-visible:[&>span:first-child]:outline peer-focus-visible:[&>span:first-child]:outline-2 peer-focus-visible:[&>span:first-child]:outline-offset-[3px] peer-focus-visible:[&>span:first-child]:outline-accent-primary">
                {selectable && <span className={classNames('grid h-6 w-6 shrink-0 place-items-center rounded border text-[#07120c]', selected ? 'border-accent-primary bg-accent-primary' : 'border-[#768791] bg-[#141b21]')} aria-hidden="true"><Check size={16} className={selected ? 'opacity-100' : 'opacity-0'} /></span>}
                {item?.win?.profit != null && <span className={classNames('ms-auto rounded-[3px] border px-[5px] py-[3px] text-[11px] font-extrabold tracking-normal', highlighted ? 'border-accent-primary/[.42] bg-accent-primary/[.08] text-accent-primary' : 'border-[#51535b] text-[#c3c4c6]')}>{item.win.profit}</span>}
              </span>}
              <span className="relative grid min-h-[115px] flex-1 place-items-center px-[9px] py-[3px] max-[540px]:min-h-[80px]">
                {revealed ? item?.win?.img ? <img className={classNames('block h-[110px] w-full max-w-[190px] object-contain [filter:drop-shadow(0_8px_10px_rgba(0,0,0,.75))_drop-shadow(0_0_13px_rgba(var(--rarity-rgb),.25))] max-[540px]:h-[76px]', !skipRequestedRef.current && 'motion-safe:animate-vault-item')} src={item.win.img} alt="" loading={index > 2 ? 'lazy' : 'eager'} /> : <Package size={36} className="text-[#a89a81]" /> : <span className="grid h-[66px] w-[66px] place-items-center rounded-full border-[3px] border-double border-[#4a8467] bg-[radial-gradient(circle,#21392b,#111815)] text-[#7ab894] shadow-[0_0_0_6px_#141d18,0_0_0_7px_#395c49] max-[540px]:h-[52px] max-[540px]:w-[52px]"><img className="block h-16 w-16 max-w-none opacity-[.85] mix-blend-screen invert max-[540px]:h-[50px] max-[540px]:w-[50px]" src="/images/S_logo_vector_black_white.svg" alt="" aria-hidden="true" /></span>}
              </span>
              {revealed && <span className="relative z-[1] flex min-h-[62px] items-end justify-between gap-[5px] px-[11px] pb-[13px] pt-1 max-[540px]:min-h-[60px] max-[540px]:flex-col max-[540px]:items-start max-[540px]:px-[9px] max-[540px]:pb-[9px] max-[540px]:pt-0.5">
                <span className="mt-0.5 block min-w-0 truncate max-[540px]:w-full"><strong className="block truncate text-[13px] font-extrabold text-white">{weapon.trim()}</strong><span className="mt-0.5 block truncate text-[12px] text-[#aaaab0]">{finish.join('|').trim()}</span></span>
                <b className="inline-flex shrink-0 items-center gap-0.5 whitespace-nowrap text-[12px] text-accent-primary tabular-nums"><Gem size={12} /> {formatPrice(itemPrice(item))}</b>
              </span>}
            </div>
          })}
        </div>
      </section>
    </div>
    <aside ref={panelRef} aria-label={t(isAnimationComplete ? 'caseOpeningMulti.selectForSale' : 'caseOpeningMulti.openingCases')} className="fixed inset-x-0 bottom-0 z-50 border-t border-white/10 bg-dark-primary px-3 pt-3 pb-[max(.5rem,env(safe-area-inset-bottom))] shadow-[0_-12px_32px_rgba(0,0,0,.35)]">
      <div className="mx-auto max-w-[600px] lg:max-w-[1160px]">
        {isAnimationComplete ? <>
          <dl className="grid grid-cols-3 gap-2 border-b border-white/10 pb-3 text-center">
            {[
              { label: t('caseOpeningMulti.spent'), value: batchCost, tone: 'text-text-secondary' },
              { label: t('caseOpeningMulti.dropValue'), value: totalPrice, tone: 'text-white' },
              { label: t('caseOpeningMulti.netResult'), value: netResult, tone: netResult == null || netResult === 0 ? 'text-text-secondary' : netResult > 0 ? 'text-accent-primary' : 'text-error', signed: true }
            ].map(({ label, value, tone, signed }) => <div key={label} className="min-w-0"><dt className="text-xs text-text-secondary">{label}</dt><dd className={`mt-1 flex flex-wrap items-center justify-center gap-1 text-sm font-bold tabular-nums sm:text-base ${tone}`}><Gem size={13} className="shrink-0" /><bdi className="[overflow-wrap:anywhere]" dir="ltr">{value == null ? '—' : `${signed && value > 0 ? '+' : ''}${formatPrice(value)}`}</bdi></dd></div>)}
          </dl>
          {saleableItems.length > 0 && <div className="mt-2 grid gap-2 lg:grid-cols-[1fr_minmax(280px,1fr)] lg:items-center">
            <div className="flex min-w-0 flex-wrap items-center justify-between gap-x-3 gap-y-1">
              <span className="inline-flex items-center gap-2 text-sm text-text-secondary"><ListChecks size={18} /><span>{t('caseOpeningMulti.selectForSale')} <bdi className="font-bold text-white" dir="ltr">{selectedItems.length} / {saleableItems.length}</bdi></span></span>
              <button type="button" onClick={handleToggleAll} disabled={isSellingSelected} className={smallButtonClassName}>{allSelected ? <X size={16} /> : <ListChecks size={16} />}{t(allSelected ? 'caseOpeningMulti.clearSelection' : 'caseOpeningMulti.selectAll')}</button>
            </div>
            <button type="button" onClick={handleSellSelected} disabled={isSellingSelected || selectedItems.length === 0} className={`${actionButtonClassName} min-w-0 bg-[linear-gradient(115deg,#00ff7f,#4effc6)] text-[#07120c]`}>{isSellingSelected ? <Loader2 size={18} className="animate-spin motion-reduce:animate-none" /> : <Gem size={18} />}{isSellingSelected ? t('inventory.selling') : t('inventory.sell')} <span className="inline-flex items-center gap-1" aria-live="polite"><Gem size={16} className="shrink-0" /><bdi className="[overflow-wrap:anywhere]" dir="ltr">{formatPrice(selectedPrice)}</bdi></span></button>
          </div>}
          <div className="mt-1 flex flex-wrap items-center justify-center gap-x-2 sm:gap-x-6">
            <button type="button" onClick={handleOpenAgain} disabled={isSellingSelected} className={actionLinkClassName}><RotateCcw size={16} />{t('caseOpeningMulti.backToCase')}</button>
            <button type="button" onClick={handleBackToInventory} disabled={isSellingSelected} className={actionLinkClassName}><Package size={16} />{t('caseOpeningMulti.toInventory')}</button>
            <button type="button" onClick={() => { hapticImpact('light'); navigate('/contract') }} disabled={isSellingSelected} className={actionLinkClassName}><FileText size={16} />{t('games.contracts')}</button>
          </div>
        </> : <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span role="status" className="inline-flex items-center gap-2 text-sm text-text-secondary"><Package size={18} /><bdi className="font-bold text-white tabular-nums" dir="ltr">{currentItemIndex} / {openingMultipleResults.length}</bdi></span>
            <div className="flex items-center gap-2">
              {!isFollowing && <button type="button" onClick={handleFollowReveal} className={`${smallButtonClassName} px-3`} aria-label={t('caseOpeningMulti.followReveal')} title={t('caseOpeningMulti.followReveal')}><LocateFixed size={18} /></button>}
              <button type="button" onClick={handleSkipReveal} className={smallButtonClassName}><ChevronsRight size={18} />{t('caseOpeningMulti.showAll')}</button>
            </div>
          </div>
          <div role="progressbar" aria-label={t('caseOpeningMulti.openingCases')} aria-valuemin={0} aria-valuemax={openingMultipleResults.length} aria-valuenow={currentItemIndex} className="mt-2 h-1 overflow-hidden rounded-full bg-white/10"><div className="h-full rounded-full bg-accent-primary transition-[width] duration-300 motion-reduce:transition-none" style={{ width: `${progress}%` }} /></div>
        </>}
      </div>
    </aside>
  </main>
}

export default CaseOpeningMulti
